import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Modal,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip as RbTooltip,
} from "react-bootstrap";
import {
  MdFilterList,
  MdAddCircle,
  MdEdit,
  MdDelete,
  MdRemoveRedEye,
} from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import loading from "../../../assets/images/common/loading.gif";
import DataTable from "react-data-table-component";

// ✅ ONLY THIS API IS USED NOW
const VOUCHER_ALL_DETAILS_ENDPOINT = "/finance/voucher/all-details/";

// Helper for approved status
const getApprovedStatusLabel = (value) => {
  if (value === true) return "Approved";
  if (value === false) return "Not Approved";

  if (value === "Y" || value === "APPROVED") return "Approved";
  if (value === "N" || value === "REJECTED") return "Not Approved";
  if (value === "P" || value === "PENDING") return "Pending";

  return value || "";
};

function VoucherEntry() {
  // UI
  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  // data
  const [rows, setRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Expandable row details state (similar to ManageBudget)
  const [rowDetails, setRowDetails] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});

  // tax(all) details modal
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxDetails, setTaxDetails] = useState(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const handleCloseTaxModal = () => setShowTaxModal(false);
  const [projects, setProjects] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);

  // env / auth
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // permissions
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());

  // --- API: load all using search_voucher_entry with all-null payload ---
  const fetchAll = async () => {
    try {
      const resp = await axios.post(baseUrl + "/search_voucher_entry/", {
        voucher_entry_id: null,
        project_id: null,
        voucher_type_id: null,
        voucher_no: null,
        cheque_no: null,
        bank_id: null,
        approved_by: null,
        approved_status: null,
        account_master_id: null,
        sub_account_master_id: null,
        party_state_id: null,
        debit_credit: null,
      });

      const data = Array.isArray(resp.data) ? resp.data : [];
      setRows(data);
    } catch (e) {
      console.error(e);
      // toast.error("Failed to fetch vouchers");
      setRows([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const resp = await axios.post(baseUrl + "/schoolmaster/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      setProjects(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setProjects([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load schools (projects)");
    }
  };

  const fetchVoucherTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/get_voucher_type/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setVoucherTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setVoucherTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load voucher types");
    }
  };

  // --- API: filtered search using search_voucher_entry ---
  const fetchFiltered = async (payload) => {
    try {
      const resp = await axios.post(baseUrl + "/search_voucher_entry/", payload, {
        headers: { "Content-Type": "application/json" },
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      setRows(data);
      setIsFilterApplied(true);
      if (!data.length) toast.info("No data found");
    } catch (e) {
      console.error(e);
      setRows([]);
      setIsFilterApplied(true);
      toast.error("Failed to fetch filtered data");
    }
  };

  // --- API: voucher details for expandable row ---
  const fetchVoucherDetails = async (voucher_entry_id) => {
    try {
      const resp = await axios.post(
        baseUrl + "/get_voucher_details_by_summary_voucher_entry_id/",
        { voucher_entry_id },
        { headers: { "Content-Type": "application/json" } }
      );

      const data = Array.isArray(resp.data) ? resp.data : [];
      return data;
    } catch (e) {
      if (e.response && e.response.status === 404) {
        toast.info("No voucher details found");
        return [];
      }
      console.error(e);
      toast.error("Failed to fetch voucher details");
      return [];
    }
  };

  // ✅ NEW: Fetch voucher all details by voucher_entry_id (GET)
  const fetchVoucherAllDetails = async (voucher_entry_id) => {
    try {
      const resp = await axios.get(
        `${baseUrl}${VOUCHER_ALL_DETAILS_ENDPOINT}${voucher_entry_id}`
      );
      return resp.data;
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 404) {
        toast.info("Voucher entry not found");
      } else {
        toast.error("Failed to fetch voucher all details");
      }
      return null;
    }
  };

  // Expandable Row Component (similar to ManageBudget)
  const ExpandedComponent = ({ data }) => {
    const details = rowDetails[data.voucher_entry_id] || [];
    const isLoading = loadingDetails[data.voucher_entry_id];

    // Helper functions for totals
    const getDebitTotal = (details = []) =>
      details.reduce((sum, d) => {
        const isDebit =
          d.debit_credit === "D" ||
          d.debit_credit === "Debit" ||
          d.debit_credit === "DEBIT";
        return sum + (isDebit ? Number(d.amount || 0) : 0);
      }, 0);

    const getCreditTotal = (details = []) =>
      details.reduce((sum, d) => {
        const isDebit =
          d.debit_credit === "D" ||
          d.debit_credit === "Debit" ||
          d.debit_credit === "DEBIT";
        return sum + (!isDebit ? Number(d.amount || 0) : 0);
      }, 0);

    return (
      <div
        className="expandedTableWrapper"
        style={{
          marginLeft: "60px",
          border: "1px solid rgb(204, 204, 204)",
          borderRadius: "8px",
          padding: "16px",
          background: "linear-gradient(135deg, rgb(249, 249, 249) 0%, rgb(230, 230, 230) 100%)",
          boxShadow: "rgba(0, 0, 0, 0.1) 2px 4px 10px",
          marginTop: "10px",
          marginBottom: "10px"
        }}
      >
        <div style={{
          marginBottom: "12px",
          fontSize: "16px",
          fontWeight: "600",
          color: "#495057",
          borderBottom: "1px solid #dee2e6",
          paddingBottom: "8px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div>
            Account Details for Voucher #{data.voucher_entry_id} - {data.voucher_no}
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleTaxEyeClick(data.voucher_entry_id)}
            disabled={taxLoading}
            title="View full tax details"
          >
            <MdRemoveRedEye style={{ marginRight: 6 }} />
            {taxLoading ? "Loading..." : "View Tax Details"}
          </Button>
        </div>

        {isLoading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <span className="ms-2">Loading voucher details...</span>
          </div>
        ) : details.length > 0 ? (
          <div style={{ overflow: "auto" }}>
            <table className="table table-bordered table-hover" style={{
              fontSize: "14px",
              margin: 0,
              minWidth: "100%"
            }}>
              <thead style={{ backgroundColor: "#e9ecef" }}>
                <tr>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>A/C Code</th>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>Sub Code</th>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>Party State</th>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600", textAlign: "right" }}>Debit Amount</th>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600", textAlign: "right" }}>Credit Amount</th>
                  <th style={{ padding: "10px", whiteSpace: "nowrap", fontWeight: "600" }}>Scope</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d) => {
                  const isDebit =
                    d.debit_credit === "D" ||
                    d.debit_credit === "Debit" ||
                    d.debit_credit === "DEBIT";
                  const debitAmount = isDebit ? d.amount : 0;
                  const creditAmount = !isDebit ? d.amount : 0;

                  return (
                    <tr key={d.voucher_details_id}>
                      <td style={{ padding: "10px" }}>{d.account_code || "-"}</td>
                      <td style={{ padding: "10px" }}>{d.sub_account_code || "-"}</td>
                      <td style={{ padding: "10px" }}>{d.statename || "-"}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{debitAmount}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>{creditAmount}</td>
                      <td style={{ padding: "10px" }}>{isDebit ? "Debit" : "Credit"}</td>
                    </tr>
                  );
                })}
                {/* Total row */}
                <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                  <td colSpan={3} style={{ padding: "10px", textAlign: "right" }}>Total:</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>{getDebitTotal(details)}</td>
                  <td style={{ padding: "10px", textAlign: "right" }}>{getCreditTotal(details)}</td>
                  <td style={{ padding: "10px" }}></td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-3" style={{ color: "#6c757d" }}>
            No account details found for this voucher
          </div>
        )}
      </div>
    );
  };

  // Handle expand click (load details when row expands)
  const handleExpandClick = async (voucher_entry_id) => {
    if (rowDetails[voucher_entry_id]) return; // Already loaded

    setLoadingDetails(prev => ({ ...prev, [voucher_entry_id]: true }));
    try {
      const details = await fetchVoucherDetails(voucher_entry_id);
      setRowDetails(prev => ({
        ...prev,
        [voucher_entry_id]: details
      }));
    } catch (err) {
      console.error("Error fetching voucher details:", err);
      toast.error("Failed to load voucher details");
    } finally {
      setLoadingDetails(prev => ({ ...prev, [voucher_entry_id]: false }));
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchVoucherTypes();
  }, []);

  // initial load
  useEffect(() => {
    setIsLoading(true);
    fetchAll().finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- actions ---
  // ✅ Edit: call all-details API, then navigate with full data
  const handleEditClick = async (voucher_entry_id) => {
    const all = await fetchVoucherAllDetails(voucher_entry_id);
    if (!all) return;

    navigate("/finance/vouchers/add", {
      state: {
        voucher: rows.find(r => r.voucher_entry_id === voucher_entry_id) || null,
        voucherAllDetails: all,
      },
    });
  };

  const handleDeleteClick = async (voucher_entry_id) => {
  const ok = window.confirm("Are you sure you want to change the status?");
  if (!ok) return;

  try {
    const resp = await axios.post(
      baseUrl + "/vouchersoft-delete/",
      {
        voucher_entry_id: voucher_entry_id,
        modified_by: userObj?.user_name || "system", // 👈 REQUIRED
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    if (resp.data?.success) {
      toast.success( "Record set to Inactive");
      fetchAll();
    } else {
      toast.error(resp.data?.message || "Unable to delete voucher");
    }
  } catch (e) {
    console.error(e);
    toast.error("Failed to delete record");
  }
};


  // ✅ Eye click: use voucher_entry_id
  const handleTaxEyeClick = async (voucher_entry_id) => {
    setTaxLoading(true);
    const data = await fetchVoucherAllDetails(voucher_entry_id);
    setTaxLoading(false);

    if (data) {
      setTaxDetails(data);
      setShowTaxModal(true);
    }
  };
  const truncateText = (text, length = 10) => {
  if (!text) return "-";
  return text.length > length ? text.slice(0, length) + "..." : text;
};


  // --- columns for main grid (using react-data-table-component) ---
  const columns = useMemo(
    () => [
      {
        name: "Project Name",
        selector: row => row.project_name,
        sortable: true,
        cell: row => (
          <Tooltip title={row.project_name}>
            <span className="cell-wrap">{truncateText(row.project_name, 14)}</span>
          </Tooltip>
        ),
        minWidth: "190px",
        
      },
      {
        name: "Voucher Type",
        selector: row => row.voucher_type_name,
        sortable: true,
        cell: row => (
          <Tooltip title={row.voucher_type_name}>
            <span className="cell-wrap">{row.voucher_type_name}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      {
        name: "Voucher No.",
        selector: row => row.voucher_no,
        sortable: true,
        cell: row => (
          <Tooltip title={row.voucher_no}>
            <span className="cell-wrap">{truncateText(row.voucher_no, 14)}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      {
        name: "Cheque No.",
        selector: row => row.cheque_no,
        sortable: true,
        cell: row => (
          <Tooltip title={row.cheque_no}>
            <span className="cell-wrap">{row.cheque_no || "-"}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      {
        name: "Bank Name",
        selector: row => row.bank_name,
        sortable: true,
        cell: row => (
          <Tooltip title={row.bank_name}>
            <span className="cell-wrap">{row.bank_name || "-"}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      {
        name: "Approved By",
        selector: row => row.approved_by,
        sortable: true,
        cell: row => (
          <Tooltip title={row.approved_by}>
            <span className="cell-wrap">{row.approved_by || "-"}</span>
          </Tooltip>
        ),
        minWidth: "120px",
      },
      {
        name: "Approved Status",
        selector: row => getApprovedStatusLabel(row.approved_status),
        sortable: true,
        cell: row => {
          const status = getApprovedStatusLabel(row.approved_status);
          return (
            <Tooltip title={status}>
              <span className="cell-wrap">{status}</span>
            </Tooltip>
          );
        },
        minWidth: "120px",
      },
      ...(canSubmit
        ? [
          {
            name: "Actions",
            cell: (row) => {
              if (!row.voucher_entry_id || row.voucher_entry_id === "No Records Found") {
                return null;
              }

              const isNotApproved = row.approved_status === false;

              return (
                <div className="tableActions">
                  {isNotApproved ? (
                    <Tooltip title="Edit" arrow>
                      <span
                        className="commonActionIcons"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleEditClick(row.voucher_entry_id);
                        }}
                      >
                        <MdEdit />
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="commonActionIcons" style={{ opacity: 0.3, cursor: "default" }}>
                      <MdEdit />
                    </span>
                  )}

                  {isNotApproved ? (
                    <Tooltip title="Delete" arrow>
                      <span
                        className="commonActionIcons"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleDeleteClick(row.voucher_entry_id);
                        }}
                      >
                        <MdDelete />
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="commonActionIcons" style={{ opacity: 0.3, cursor: "default" }}>
                      <MdDelete />
                    </span>
                  )}
                </div>
              );
            },
            minWidth: "120px",
            center: true,
          },
        ]
        : [])
    ],
    [canSubmit]
  );

  // --- search (client-side) ---
  const searchable = [
    (r) => r.project_name,
    (r) => r.voucher_type_name,
    (r) => r.voucher_no,
    (r) => r.cheque_no,
    (r) => r.bank_name,
    (r) => r.approved_by,
    (r) => getApprovedStatusLabel(r.approved_status),
  ];

  const filteredRows = (rows || []).filter((row) =>
    searchable.some((sel) =>
      String(sel(row) || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // --- Filter Modal state ---
  const [form, setForm] = useState({
    project_id: "",
    voucher_type_id: "",
    voucher_no: "",
    cheque_no: "",
    bank_name: "",
    approved_status: "",
    from_date: "",
    to_date: "",
  });

  const handleFilterSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      voucher_entry_id: null,
      project_id: form.project_id || null,
      voucher_type_id: form.voucher_type_id
        ? Number(form.voucher_type_id)
        : null,
      voucher_no: form.voucher_no || null,
      cheque_no: form.cheque_no || null,
      bank_id: form.bank_name || null,
      approved_by: null,
      approved_status: form.approved_status || null,
      account_master_id: null,
      sub_account_master_id: null,
      party_state_id: null,
      debit_credit: null,
    };

    setIsLoading(true);
    await fetchFiltered(payload);
    setIsLoading(false);
    setShowFilterModal(false);
    setRowDetails({}); // Clear details when filtering
  };

  const handleReset = async () => {
    setForm({
      project_id: "",
      voucher_type_id: "",
      voucher_no: "",
      cheque_no: "",
      bank_name: "",
      approved_status: "",
      from_date: "",
      to_date: "",
    });
    setIsLoading(true);
    setIsFilterApplied(false);
    setRowDetails({}); // Clear details when resetting
    fetchAll().finally(() => setIsLoading(false));
  };

  const safe = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  return (
    <div className="pageBody">
      <ToastContainer />

      {/* Table Head */}
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Voucher Entry</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={handleSearchChange}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger
              placement="top"
              overlay={<RbTooltip id="tooltip-top">Filter</RbTooltip>}
            >
              <Button
                className="secondaryBtn"
                variant="secondary"
                onClick={handleShowFilterModal}
              >
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            {canSubmit && (
              <OverlayTrigger
                placement="top"
                overlay={<RbTooltip id="tooltip-top">Add</RbTooltip>}
              >
                <Button
                  className="primaryBtn"
                  variant="primary"
                  onClick={() => navigate("/finance/vouchers/add")}
                >
                  <MdAddCircle />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        </div>
      </div>

      {/* Table Body */}
      <div className="commonTable height100">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <DataTable
              className="custom-table"
              columns={columns}
              data={
                Array.isArray(filteredRows) && filteredRows.length > 0
                  ? filteredRows
                  : [
                    {
                      voucher_entry_id: "No Records Found",
                      project_name: "",
                      voucher_type_name: "",
                      voucher_no: "",
                      cheque_no: "",
                      bank_name: "",
                      approved_by: "",
                      approved_status: "",
                    }
                  ]
              }
              expandableRows={
                Array.isArray(filteredRows) && filteredRows.length > 0
              }
              expandableRowsComponent={ExpandedComponent}
              expandableRowExpanded={(row) => row.voucher_entry_id && rowDetails[row.voucher_entry_id]}
              onRowExpandToggled={(expanded, row) => {
                if (expanded && row.voucher_entry_id) {
                  handleExpandClick(row.voucher_entry_id);
                }
              }}
              expandableRowsHideExpander={false}
              pagination={
                Array.isArray(filteredRows) && filteredRows.length > 0
              }
              highlightOnHover
              paginationPerPage={10}
              responsive
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 250px)"
              conditionalRowStyles={[
                {
                  when: (row) => row.voucher_entry_id === "No Records Found",
                  style: {
                    textAlign: "center",
                    fontSize: "16px",
                    color: "red",
                    backgroundColor: "#f9f9f9",
                  },
                },
              ]}
              customStyles={{
                headRow: {
                  style: {
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #dee2e6",
                    fontWeight: "600",
                  },
                },
                rows: {
                  style: {
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f5f5f5",
                    },
                  },
                },
                expanderButton: {
                  style: {
                    cursor: "pointer",
                    fontSize: "20px",
                    fontWeight: "bold",
                  },
                },
              }}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        show={showFilterModal}
        onHide={handleCloseFilterModal}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Search</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form id="filterForm" onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className='commonInput'>
                  <Form.Group controlId="month_name">
                    <Form.Label>Project</Form.Label>
                    <Form.Select
                      name="project_id"
                      id="project_id"
                      value={form.project_id}
                      onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                    >
                      <option value="" disabled hidden>Select Project</option>
                      {(projects || [])
                        .filter((project) => project.status === "Active")
                        .map((project) => (
                          <option key={project.school_id} value={project.school_id}>
                            {project.school_name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>

              <Col xs={12}>
                <div className='commonInput'>
                  <Form.Group controlId="month_name">
                    <Form.Label>Voucher Type</Form.Label>
                    <Form.Select
                      name="voucher_type_id"
                      id="voucher_type_id"
                      value={form.voucher_type_id}
                      onChange={(e) => setForm({ ...form, voucher_type_id: e.target.value })}
                    >
                      <option value="" disabled hidden>Select Voucher Type</option>
                      {(voucherTypes || [])
                        .map((voucherType) => (
                          <option key={voucherType.voucher_type_id} value={voucherType.voucher_type_id}>
                            {voucherType.voucher_type_name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
            Reset
          </Button>
          <div>
            <Button
              variant="secondary"
              className="btn-danger secondaryBtn me-2"
              onClick={handleCloseFilterModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn-success primaryBtn"
              type="submit"
              form="filterForm"
            >
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Tax Details Modal */}
      <Modal show={showTaxModal} onHide={handleCloseTaxModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Voucher All Details (Tax / GST / TDS / TCS)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {taxLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (() => {
            const vd = taxDetails?.lstvoucher_details?.[0] || {};
            const v = vd?.objvoucher_details || {};
            const gstList = vd?.lstvoucher_gst_details || [];

            // if your API later adds these keys, it will show automatically
            const tds = vd?.objvoucher_tds_details || null;
            const tcs = vd?.objvoucher_tcs_details || null;

            const gstTypeName = (g) => {
              // map ids if you want: 1=SGST,2=CGST,3=IGST,4=UTGST,5=CESS (example)
              const map = { 1: "SGST", 2: "CGST", 3: "IGST", 4: "UTGST", 5: "CESS" };
              return map[g?.gst_type_id] || `GST-${safe(g?.gst_type_id)}`;
            };

            return (
              <>
                <div style={{ border: "1px solid #0d6efd", borderRadius: 6, marginBottom: 16 }}>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #dee2e6" }}>
                    <strong>TDS</strong>
                  </div>

                  <div style={{ padding: "14px" }}>
                    {tds ? (
                      <Row className="g-3">
                        <Col md={4}>
                          <div><strong>TDS Code :</strong> {safe(tds?.tds_account_code)}</div>
                        </Col>
                        <Col md={4}>
                          <div><strong>TDS Party :</strong> {safe(tds?.tds_sub_account_code)}</div>
                        </Col>
                        <Col md={4}>
                          <div><strong>Advance Recovery :</strong> {safe(tds?.advance_recovery)}</div>
                        </Col>

                        <Col md={4}>
                          <div><strong>TDS Percentage :</strong> {safe(tds?.tds_percentage)}</div>
                        </Col>
                        <Col md={4}>
                          <div><strong>TDS Amount :</strong> {safe(tds?.tds_amount)}</div>
                        </Col>
                      </Row>
                    ) : (
                      <div style={{ color: "#6c757d" }}>No TDS details.</div>
                    )}
                  </div>
                </div>

                {/* GST BOX */}
                <div style={{ border: "1px solid #0d6efd", borderRadius: 6 }}>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid #dee2e6" }}>
                    <strong>GST</strong>
                  </div>

                  <div style={{ padding: "14px" }}>
                    <Row className="g-3" style={{ marginBottom: 10 }}>
                      <Col md={6}>
                        <div><strong>Code Type :</strong> {safe(gstList?.[0]?.code_type_id)}</div>
                      </Col>
                      <Col md={6}>
                        <div><strong>HSN/SAC Code :</strong> {safe(gstList?.[0]?.hsn_sac_master_id)}</div>
                      </Col>
                    </Row>

                    <div className="table-responsive">
                      <table className="table table-bordered table-sm mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: 140 }}></th>
                            <th style={{ textAlign: "right" }}>Tax %</th>
                            <th style={{ textAlign: "right" }}>Tax Amount</th>
                            <th style={{ textAlign: "right" }}>Tax Adjustment</th>
                            <th style={{ textAlign: "right" }}>Net Tax</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gstList.length === 0 ? (
                            <tr>
                              <td colSpan={5} style={{ textAlign: "center" }}>No GST details</td>
                            </tr>
                          ) : (
                            gstList.map((g, idx) => (
                              <tr key={idx}>
                                <td>{gstTypeName(g)}</td>
                                <td style={{ textAlign: "right" }}>{safe(g?.tax_percentage)}</td>
                                <td style={{ textAlign: "right" }}>{safe(g?.amount)}</td>
                                <td style={{ textAlign: "right" }}>{safe(g?.adjustment)}</td>
                                <td style={{ textAlign: "right" }}>{safe(g?.nettaxamount)}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Optional: show which voucher detail is being displayed */}
                    <div style={{ paddingTop: 8, fontSize: 12, color: "#6c757d" }}>
                      Voucher Details ID: {safe(v?.voucher_details_id)} | Debit/Credit: {safe(v?.debit_credit)}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseTaxModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default VoucherEntry;